import { skipToken } from '@reduxjs/toolkit/query'
import { useGetMcpApplicationsByClientQuery } from 'core/api/application'
import { McpApplicationListItem } from 'core/interfaces/Application'
import { SelectedMcpApplication } from 'lauria/types/mcpApplication'
import { ChangeEvent, Dispatch, SetStateAction, useMemo, useState } from 'react'

export type ApplicationSelectOption = {
  value: string
  label: string
}

type UseMcpApplicationMultiSelectParams = {
  idClient: number | null
  onSelect: Dispatch<SetStateAction<SelectedMcpApplication[]>>
  isApplicationExcluded: (application: McpApplicationListItem) => boolean
}

export const useMcpApplicationMultiSelect = ({
  idClient,
  onSelect,
  isApplicationExcluded,
}: UseMcpApplicationMultiSelectParams) => {
  const [selectValue, setSelectValue] = useState('')

  const { data: mcpApplications = [], isLoading } =
    useGetMcpApplicationsByClientQuery(
      idClient !== null ? { idClient } : skipToken
    )

  const options = useMemo<ApplicationSelectOption[]>(() => {
    return mcpApplications
      .filter((application) => !isApplicationExcluded(application))
      .map((application) => ({
        value: application.id.toString(),
        label: application.nomTechnique,
      }))
  }, [isApplicationExcluded, mcpApplications])

  const handleSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value

    if (selectedValue === '') {
      return
    }

    const applicationId = Number(selectedValue)
    const selectedOption = options.find(
      (option) => option.value === selectedValue
    )

    if (!selectedOption) {
      return
    }

    onSelect((previousSelection) => {
      const alreadySelected = previousSelection.some(
        (item) => item.id === applicationId
      )

      if (alreadySelected) {
        return previousSelection
      }

      return [
        ...previousSelection,
        {
          id: applicationId,
          label: selectedOption.label,
        },
      ]
    })

    setSelectValue('')
  }

  return {
    options,
    isLoading,
    selectValue,
    handleSelect,
  }
}
